import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircle, faSuitcase } from '@fortawesome/free-solid-svg-icons'
import { faMapMarker } from '@commutifi-fe/custom-icons/solid'
import { Timeline } from 'antd'
import { TimelineItemProps, TimelineProps } from 'antd/es/timeline'
import cx from 'classnames'
import React from 'react'
import { Button, AppButtonProps } from '../AppButton'
import './styles.scss'
import { colors } from '../style/constants'
import { useLocaleReceiver } from '../locales/LocaleReceiver'

export type { TimelineItemProps, TimelineProps }

const clsPrefix = 'app-timeline'

export interface CommuteTimelineProps extends TimelineProps {
  // Antd form item id
  id?: string
}

export interface CommuteTimelineItemProps extends TimelineItemProps {
  children?: React.ReactNode | React.ReactElement
}

interface NodeDescriptionProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
}

interface EndpointsItemProps extends TimelineItemProps {
  // Title of the timeline dot/node
  title: React.ReactNode
}

export function CommuteTimelineStepDescription({ title, subtitle }: NodeDescriptionProps) {
  return (
    <div className="u-margin-left--tiny">
      <div className="u-color--neutral-600 u-font-weight-bold">{title || <br />}</div>
      {subtitle ? <div className="u-color--neutral-500 u-font-12">{subtitle}</div> : null}
    </div>
  )
}

export function CommuteTimeline({ className, ...props }: CommuteTimelineProps) {
  return <Timeline className={cx(clsPrefix, className)} {...props} />
}

function InterStepDot() {
  return <FontAwesomeIcon icon={faCircle} size="xs" color={colors.neutral400} />
}
const CommuteTimelineItem = ({ children, ...props }: CommuteTimelineItemProps): TimelineItemProps => ({
  dot: <InterStepDot />,
  children,
  className: `${clsPrefix}-new-item-head`,
  ...props
})

function StartDot() {
  return <FontAwesomeIcon icon={faMapMarker} color={colors.neutral400} />
}

function StartNode({ title }: EndpointsItemProps) {
  const intl = useLocaleReceiver('CommuteTimeline')
  return <CommuteTimelineStepDescription title={title} subtitle={intl.formatMessage({ id: 'startDotLabel' })} />
}
function startNodeConfig({ title, ...props }: EndpointsItemProps): TimelineItemProps {
  return {
    dot: <StartDot />,
    children: <StartNode title={title} />,
    ...props
  }
}

function EndDot() {
  return <FontAwesomeIcon icon={faSuitcase} color={colors.neutral400} />
}

function EndNode({ title }: EndpointsItemProps) {
  const intl = useLocaleReceiver('CommuteTimeline')
  return <CommuteTimelineStepDescription title={title} subtitle={intl.formatMessage({ id: 'endDotLabel' })} />
}
function endNodeConfig({ title, ...props }: EndpointsItemProps): TimelineItemProps {
  return {
    dot: <EndDot />,
    children: <EndNode title={title} />,
    ...props
  }
}

const TransitionNode = ({ children, className, ...props }: CommuteTimelineItemProps = {}): TimelineItemProps => ({
  className: cx(`${clsPrefix}-transition-item`, className),
  children,
  ...props
})

const AddNodeButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(({ children, ...buttonProps }, ref) => {
  const intl = useLocaleReceiver('CommuteTimeline')
  return (
    <Button type="primary" ref={ref} {...buttonProps}>
      <span className={`${clsPrefix}-transition-item-button-content`}>
        {children || intl.formatMessage({ id: 'addStep' })}
      </span>
    </Button>
  )
})
AddNodeButton.displayName = 'AddNodeButton'
function addNodeButtonConfig(props: AppButtonProps): TimelineItemProps {
  return {
    className: `${clsPrefix}-transition-item-button-container`,
    children: <AddNodeButton {...props} />
  }
}

CommuteTimeline.NodeConfig = CommuteTimelineItem
CommuteTimeline.StartNodeConfig = startNodeConfig
CommuteTimeline.TransitionNodeConfig = TransitionNode
CommuteTimeline.EndNodeConfig = endNodeConfig
CommuteTimeline.AddNodeButtonConfig = addNodeButtonConfig
CommuteTimeline.isTimelineItem = (item: TimelineItemProps | null): item is TimelineItemProps => Boolean(item)

export default CommuteTimeline
